import { readContract, WriteContractParameters } from '@wagmi/core';
import { useMemo } from 'react';
import { erc20Abi } from 'viem';
import { useConnection, useReadContract } from 'wagmi';

import { config } from '@/app/providers/app/wagmiConfig';
import { MZUSD_DECIMALS, USDC_DECIMALS } from '@/app/shared/constants/common';
import { VaultTrancheId } from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useChainContracts } from '@/app/shared/hooks/blockchain/useChainContracts';
import {
  SequentialStepPlan,
  useSendSequentialSteps,
} from '@/app/shared/hooks/blockchain/useSendSequentialSteps/useSendSequentialSteps';
import { useDebounce } from '@/app/shared/hooks/useDebounce';
import { makeApproveTx } from '@/app/shared/lib/blockchain/makeApproveTx';
import { safeParseUnits } from '@/app/shared/lib/safeParseUnits';

interface UseVaultDepositSequentialArgs {
  amount: string;
  trancheId: VaultTrancheId;
  vaultId?: string;
  trancheSymbol?: string;
  enabled?: boolean;
}

const mzusdToUsdc = (mzusdAmount: bigint) => mzusdAmount / 10n ** 12n;

export const useVaultDepositSequential = ({
  amount,
  trancheId,
  vaultId,
  trancheSymbol = 'tranche',
  enabled = true,
}: UseVaultDepositSequentialArgs) => {
  const { address, isConnected } = useConnection();
  const contracts = useChainContracts(vaultId);
  const debouncedAmount = useDebounce(amount, 500);
  const amountUsdc = safeParseUnits(debouncedAmount, USDC_DECIMALS);
  const amountMzusd = safeParseUnits(debouncedAmount, MZUSD_DECIMALS);
  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;
  const { data: mzusdBalance, refetch: refetchMzusdBalance } = useReadContract({
    address: contracts?.MZUSD,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: enabled && !!contracts?.MZUSD && !!address },
  });
  const { data: usdcAllowance, refetch: refetchUsdcAllowance } = useReadContract({
    address: contracts?.ASSET,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && contracts ? [address, contracts.MZUSD] : undefined,
    query: { enabled: enabled && !!contracts?.ASSET && !!address },
  });
  const { data: mzusdAllowance, refetch: refetchMzusdAllowance } = useReadContract({
    address: contracts?.MZUSD,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && trancheAddress ? [address, trancheAddress] : undefined,
    query: { enabled: enabled && !!contracts?.MZUSD && !!trancheAddress && !!address },
  });
  const { needUsdc, plannedSteps } = useMemo(() => {
    if (
      !enabled ||
      !contracts ||
      !address ||
      !amountUsdc ||
      !amountMzusd ||
      !trancheAddress ||
      mzusdBalance === undefined ||
      usdcAllowance === undefined ||
      mzusdAllowance === undefined
    ) {
      return { needUsdc: 0n, plannedSteps: [] as SequentialStepPlan[] };
    }
    const ownedMzusd = mzusdBalance;
    const needMzusd = amountMzusd > ownedMzusd ? amountMzusd - ownedMzusd : 0n;
    const usdcNeeded = needMzusd > 0n ? mzusdToUsdc(needMzusd) : 0n;
    const hasEnoughMzusd = usdcNeeded === 0n;
    const depositAmount = hasEnoughMzusd
      ? ownedMzusd < amountMzusd
        ? ownedMzusd
        : amountMzusd
      : amountMzusd;
    const approveUsdcAmount = usdcNeeded > 0n ? usdcNeeded : amountUsdc;
    const mintUsdcAmount = usdcNeeded > 0n ? usdcNeeded : amountUsdc;
    const skipUsdcApprove = hasEnoughMzusd;
    const resolveDepositTx = async (): Promise<WriteContractParameters> => {
      const liveBalance = await readContract(config, {
        address: contracts.MZUSD,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address],
      });
      const liveDeposit = liveBalance < amountMzusd ? liveBalance : amountMzusd;
      if (liveDeposit <= 0n) {
        throw new Error('Insufficient MZUSD balance for deposit');
      }
      return {
        address: trancheAddress,
        abi: ABI.TRANCHE,
        functionName: 'deposit',
        args: [liveDeposit, address],
      };
    };
    const steps: SequentialStepPlan[] = [
      {
        id: 'approve-usdc',
        title: 'Approve deposit asset',
        description: 'Approve asset spending for the MZUSD contract',
        isComplete: skipUsdcApprove,
        tx: makeApproveTx({
          token: contracts.ASSET,
          spender: contracts.MZUSD,
          amount: approveUsdcAmount,
        }) as WriteContractParameters,
      },
      {
        id: 'mint-mzusd',
        title: 'Get MZUSD',
        description: 'Deposit asset to receive MZUSD',
        isComplete: hasEnoughMzusd,
        tx: {
          address: contracts.MZUSD,
          abi: ABI.MZUSD,
          functionName: 'deposit',
          args: [mintUsdcAmount],
        },
      },
      {
        id: 'approve-mzusd',
        title: 'Approve MZUSD',
        description: `Approve MZUSD spending for the ${trancheSymbol} contract`,
        isComplete: false,
        tx: makeApproveTx({
          token: contracts.MZUSD,
          spender: trancheAddress,
          amount: amountMzusd,
        }) as WriteContractParameters,
      },
      {
        id: 'deposit-tranche',
        title: `Deposit to ${trancheSymbol}`,
        description: `Deposit MZUSD to receive ${trancheSymbol}`,
        isComplete: false,
        tx: {
          address: trancheAddress,
          abi: ABI.TRANCHE,
          functionName: 'deposit',
          args: [depositAmount, address],
        },
        resolveTx: resolveDepositTx,
      },
    ];
    return { needUsdc: usdcNeeded, plannedSteps: steps };
  }, [
    address,
    amountMzusd,
    amountUsdc,
    contracts,
    enabled,
    mzusdAllowance,
    mzusdBalance,
    trancheAddress,
    trancheSymbol,
    usdcAllowance,
  ]);
  const planKey = `${debouncedAmount}:${trancheId}:${trancheAddress ?? ''}:${address ?? ''}`;
  const sequential = useSendSequentialSteps({
    steps: plannedSteps,
    planKey,
    enabled: enabled && isConnected && plannedSteps.length > 0,
  });
  const refetchPlanState = async () => {
    await Promise.all([refetchMzusdBalance(), refetchUsdcAllowance(), refetchMzusdAllowance()]);
  };
  const isReady =
    enabled &&
    isConnected &&
    !!address &&
    !!amountUsdc &&
    !!amountMzusd &&
    plannedSteps.length > 0 &&
    mzusdBalance !== undefined &&
    usdcAllowance !== undefined &&
    mzusdAllowance !== undefined;
  return {
    ...sequential,
    amountUsdc,
    amountMzusd,
    needUsdc,
    trancheAddress,
    contracts,
    debouncedAmount,
    isReady,
    plannedSteps,
    refetchPlanState,
    isBatchingTransaction: false as const,
  };
};
